import React from "react";

import { ModuleSpec } from "src/api/workspacesV2Api";
import { ModuleStatus, WorkspaceDb } from "src/clients/flow-api";
import { COLORS } from "src/utils/constants";

type ModulesVersionBarProps = {
  workspaces: WorkspaceDb[];
  moduleType: string;
  selectedVersion: string | null;
  onSelectVersion: (version: string | null) => void;
  allVersions?: string[];
};

export const ModulesVersionBar: React.FC<ModulesVersionBarProps> = ({
  workspaces,
  moduleType,
  selectedVersion,
  onSelectVersion,
  allVersions,
}) => {
  // Count workspaces per version for this specific module
  const versionCounts: Record<string, number> = {};
  workspaces.forEach((ws) => {
    const modules = ws.modules as Record<string, ModuleSpec>;
    const module = modules?.[moduleType];
    if (module?.version) {
      versionCounts[module.version] = (versionCounts[module.version] || 0) + 1;
    }
  });

  const total = Object.values(versionCounts).reduce((a, b) => a + b, 0);

  // If no workspaces have this module, don't render anything
  if (total === 0) {
    return null;
  }

  // Use allVersions for color mapping if provided, else use current versions
  const versions = Object.keys(versionCounts);
  const colorVersions = allVersions || versions;
  const colors = COLORS;

  // Map version to color index based on allVersions
  const versionColorIndex: Record<string, number> = {};
  colorVersions.forEach((v, i) => {
    versionColorIndex[v] = i;
  });

  // For each version, count failed workspaces
  const versionFailureCounts: Record<string, number> = {};
  workspaces.forEach((ws) => {
    const modules = ws.modules as Record<string, ModuleSpec>;
    const module = modules?.[moduleType];
    if (
      module?.version &&
      module.status &&
      (module.status as ModuleStatus).toLowerCase().includes("failed")
    ) {
      versionFailureCounts[module.version] =
        (versionFailureCounts[module.version] || 0) + 1;
    }
  });

  return (
    <div className="mb-4 w-full rounded-lg border border-gray-200 p-4">
      <h3 className="mb-2 font-inter-semibold-14px text-gray-900">
        Desired module versions overview
      </h3>
      <p className="mb-2 font-inter-normal-12px text-gray-600">
        These are the desired module versions i.e. the versions set in flowDB
        not necessarily the actual versions in the workspaces.
      </p>
      <div className="flex h-6 w-full overflow-hidden rounded border border-gray-200">
        {versions.map((version) => {
          const totalCount = versionCounts[version];
          const failCount = versionFailureCounts[version] || 0;
          const okCount = totalCount - failCount;
          const color = colors[versionColorIndex[version] % colors.length];
          const widthPercent = (totalCount / total) * 100;
          const failPercent =
            failCount > 0 ? (failCount / totalCount) * 100 : 0;
          const okPercent = 100 - failPercent;

          return (
            <div
              key={version}
              className="h-full"
              style={{
                width: `${widthPercent}%`,
                position: "relative",
                height: "100%",
              }}
            >
              {/* Normal (ok) part */}
              <button
                aria-pressed={selectedVersion === version}
                className="h-full transition-all duration-300 focus:outline-none"
                style={{
                  width: "100%",
                  height: "100%",
                  background: color,
                  border: "none",
                  borderRadius: 0,
                  cursor: "pointer",
                  outline:
                    selectedVersion === version ? "2px solid #222" : undefined,
                  outlineOffset: "-2px",
                  position: "absolute",
                  left: 0,
                  top: 0,
                  zIndex: 1,
                  // If there are failures, only fill the ok part
                  clipPath:
                    failCount > 0
                      ? `inset(0 ${100 - okPercent}% 0 0)`
                      : undefined,
                }}
                title={`${version}: ${okCount} healthy workspaces`}
                type="button"
                onClick={() => onSelectVersion(version)}
              />
              {/* Failure (dashed/orange) part */}
              {failCount > 0 && (
                <button
                  aria-pressed={selectedVersion === version}
                  className="h-full transition-all duration-300 focus:outline-none"
                  style={{
                    width: "100%",
                    height: "100%",
                    background: `repeating-linear-gradient(135deg, ${color}, ${color} 6px, #fff 6px, #fff 12px)`,
                    border: "none",
                    borderRadius: 0,
                    cursor: "pointer",
                    outline:
                      selectedVersion === version
                        ? "2px solid #222"
                        : undefined,
                    outlineOffset: "-2px",
                    position: "absolute",
                    left: 0,
                    top: 0,
                    zIndex: 2,
                    opacity: 1,
                    clipPath: `inset(0 0 0 ${okPercent}%)`,
                  }}
                  title={`${version}: ${failCount} failed workspaces`}
                  type="button"
                  onClick={() => onSelectVersion(version)}
                />
              )}
            </div>
          );
        })}
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        {selectedVersion
          ? colorVersions
              .filter(
                (version) =>
                  version === selectedVersion && versionCounts[version] > 0,
              )
              .map((version) => (
                <button
                  key={version}
                  aria-pressed={true}
                  className="flex items-center gap-1 rounded bg-indigo-50 px-2 py-1 font-inter-normal-12px ring-2 ring-indigo-500"
                  style={{ cursor: "pointer" }}
                  type="button"
                  onClick={() => onSelectVersion(version)}
                >
                  <span
                    className="inline-block h-3 w-3 rounded"
                    style={{
                      background:
                        colors[versionColorIndex[version] % colors.length],
                    }}
                  />
                  <span className="font-mono">{version}</span>
                  <span className="text-gray-500">
                    ({versionCounts[version] || 0})
                  </span>
                </button>
              ))
          : colorVersions
              .filter((version) => versionCounts[version] > 0)
              .map((version) => (
                <button
                  key={version}
                  aria-pressed={selectedVersion === version}
                  className={`flex items-center gap-1 rounded px-2 py-1 font-inter-normal-12px ${selectedVersion === version ? "bg-indigo-50 ring-2 ring-indigo-500" : ""}`}
                  style={{ cursor: "pointer" }}
                  type="button"
                  onClick={() => onSelectVersion(version)}
                >
                  <span
                    className="inline-block h-3 w-3 rounded"
                    style={{
                      background:
                        colors[versionColorIndex[version] % colors.length],
                    }}
                  />
                  <span className="font-mono">{version}</span>
                  <span className="text-gray-500">
                    ({versionCounts[version] || 0})
                  </span>
                </button>
              ))}
        {selectedVersion && (
          <button
            className="ml-2 font-inter-normal-12px text-gray-600 underline"
            type="button"
            onClick={() => onSelectVersion(null)}
          >
            Clear filter
          </button>
        )}
      </div>
    </div>
  );
};
